import { useCallback, useEffect, useState } from 'react'
import {
  ShieldCheck, Power, LogOut, ArrowLeft, KeyRound, ScrollText, BookmarkPlus, Lock, Sparkles,
} from 'lucide-react'
import { Tabs, apiGet, apiMutate, useToast, usePermissions } from './primitives.js'
import { permMeta, auditMeta } from './catalog.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { Link, navigate } from '../router.js'
import { deviceSummary } from '../ProfilePage.js'
import {
  UserModulesBlock, UserExceptionsBlock, UserRightsHistory, ApplyTemplatesModal, SaveAsTemplateModal,
  shapeRights, type RightsOverview,
} from './UserRights.js'
import { UserNotificationPrefsPanel } from './UserNotificationPrefsPanel.js'
import './droits.css'

/**
 * /settings/users/:id — la FICHE UTILISATEUR pleine page (LOT 5.5).
 *
 * Remplace le drawer étroit du LOT 4 comme interface principale : header
 * identitaire hiérarchisé, onglets (Compte · Sécurité · Rôles & permissions ·
 * Sessions · Accès futurs · Audit), et les actions SENSIBLES en bas de page
 * — plus de gros bouton rouge dominant dans le header. Chaque action
 * explique sa conséquence avant confirmation.
 *
 * Mêmes endpoints et mêmes gardes que le LOT 4 (requireAdminMutation côté
 * serveur) : aucune règle RBAC n'a changé, seule l'interface.
 *
 * DROITS & MODÈLES (08/10/2026) — l'onglet « Droits » dit, dans l'ordre où on
 * se pose la question : quels MODÈLES ce compte applique (plusieurs possibles,
 * union), ce que cela OUVRE module par module et d'où vient chaque droit, ses
 * EXCEPTIONS (cases par permission, qui l'emportent), ce que le moteur accorde
 * au final, et l'HISTORIQUE. Changer les modèles passe par un aperçu (droits
 * gagnés / perdus, motif si un droit critique est gagné, refus de délégation
 * annoncé avant de cliquer). « Enregistrer comme modèle » reprend les droits
 * effectifs du compte. L'onglet « Notifications » accueille le panneau du
 * chantier notifications (contrat : UserNotificationPrefsPanel, { userId }).
 */

interface UserRow {
  id: string; email: string; display_name: string; status: string
  mfa_enabled: boolean; mfa_required: boolean
  roles: { key: string; name: string }[]
  last_login_at: string | null; active_sessions: number; created_at: string
}
interface Detail {
  user: UserRow
  sessions: { id: string; current: boolean; created_at: string; last_seen_at: string; expires_at: string; ip: string | null; user_agent: string | null }[]
  service_catalogs: { service_key: string; service_name: string; permissions: number }[]
  security: { must_change_password: boolean; has_password: boolean; recovery_codes_remaining: number; password_updated_at: string | null }
  permissions: string[]
  /** SIRH LOT 4.8 (§26-31) — livrés par l'API à partir de ce lot. */
  assigned_roles?: unknown
  effective?: unknown
}
interface AuditItem {
  id: number; occurred_at: string; event_type: string; severity: string
  human_message: string | null; actor_id: string | null; is_actor: boolean
}

function fmt(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function statutPill(status: string) {
  if (status === 'ACTIVE') return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok" />Actif</span>
  if (status === 'SUSPENDED') return <span className="lc-pill lc-pill--warning">Activation à finaliser</span>
  return <span className="lc-pill lc-pill--error">Désactivé</span>
}

/* ═══════════ SIRH LOT 4.8 — ASSIGNÉ ≠ EFFECTIF (§26-31) ═══════════
   Deux natures de données, deux blocs, jamais confondus :
     · « Rôles assignés » vient de `user_role` — une donnée d'AFFECTATION
       légitime (§28), qui ne dit PAS ce que le compte peut faire ;
     · « Droits effectifs » vient du MOTEUR (gates ∩ leaf ∩ override ∩
       scope). Une jointure `user_role ⋈ role_permission` n'est jamais
       présentée comme la vérité effective (§29).
   Tout est gardé en forme : si le serveur ne renvoie pas encore `effective`
   (déploiement en cours), l'écran le DIT — il n'affiche pas un faux
   « autorisé » construit à partir des rôles. */

interface AssignedRole { key: string; name: string; permissions: number }
type EffScope = 'ALL' | 'SELF' | 'TEAM' | 'PERSON' | 'UNION'
interface ScopedRight {
  permission: string
  label: string
  scope: EffScope
  /** `null` = tout le monde — jamais un nombre fabriqué. */
  count: number | null
  /** Source humaine si le serveur la fournit (« Responsable exploitation »). */
  via?: string | undefined
}
interface Effective {
  allowed: string[]
  denied_by_override: string[]
  blocked_by_gate: string[]
  scoped: ScopedRight[]
}

const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []

export function shapeAssignedRoles(v: unknown): AssignedRole[] | null {
  if (!Array.isArray(v)) return null
  return v.flatMap((r) => {
    if (!r || typeof r !== 'object') return []
    const o = r as Record<string, unknown>
    if (typeof o['key'] !== 'string' || typeof o['name'] !== 'string') return []
    return [{ key: o['key'], name: o['name'], permissions: typeof o['permissions'] === 'number' ? o['permissions'] : 0 }]
  })
}

const EFF_SCOPES = ['ALL', 'SELF', 'TEAM', 'PERSON', 'UNION'] as const
const isEffScope = (v: unknown): v is EffScope => typeof v === 'string' && (EFF_SCOPES as readonly string[]).includes(v)

export function shapeEffective(v: unknown): Effective | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  // Sans `allowed`, le moteur n'a rien dit : on ne devine pas à sa place.
  if (!Array.isArray(o['allowed'])) return null
  const scoped: ScopedRight[] = Array.isArray(o['scoped'])
    ? o['scoped'].flatMap((s) => {
        if (!s || typeof s !== 'object') return []
        const e = s as Record<string, unknown>
        if (typeof e['permission'] !== 'string' || !isEffScope(e['scope'])) return []
        const count = typeof e['count'] === 'number' ? e['count'] : null
        const via = typeof e['via'] === 'string' ? e['via'] : undefined
        return [{
          permission: e['permission'],
          label: typeof e['label'] === 'string' && e['label'] ? e['label'] : permMeta(e['permission']).label,
          scope: e['scope'], count, ...(via !== undefined ? { via } : {}),
        }]
      })
    : []
  return {
    allowed: strList(o['allowed']),
    denied_by_override: strList(o['denied_by_override']),
    blocked_by_gate: strList(o['blocked_by_gate']),
    scoped,
  }
}

const SCOPE_HUMAN: Record<EffScope, string> = {
  ALL: 'Tout le monde', SELF: 'Lui-même', TEAM: 'Son équipe',
  PERSON: 'Personnes désignées', UNION: 'Périmètre combiné',
}

/** « Autorisé — via Responsable exploitation · Son équipe · 23 personnes » (§31). */
export function effectiveSentence(s: ScopedRight | undefined): string {
  if (!s) return 'Autorisé'
  const parts: string[] = []
  if (s.via) parts.push(`via ${s.via}`)
  parts.push(SCOPE_HUMAN[s.scope])
  if (s.count === null) { if (s.scope !== 'ALL') parts.push('Tout le monde') }
  else parts.push(`${s.count} personne${s.count > 1 ? 's' : ''}`)
  return `Autorisé — ${parts.join(' · ')}`
}

/** §30 : un rôle accorde, un override refuse — l'écran dit REFUSÉ. */
const DENIED_SENTENCE = 'Refusé — refus direct'
const GATED_SENTENCE = 'Refusé — bloqué par un accès de famille ou de module'

const PERM_GROUP_LABELS: Record<string, string> = {
  user: 'Comptes', role: 'Rôles', service: 'Services', integration: 'Intégrations',
  alert: 'Alertes', audit: 'Audit', core: 'Portail', system: 'Système',
  access: 'RH', person: 'RH', rule: 'RH', sync: 'RH', override: 'RH',
  security: 'Hubs', parc: 'Hubs', communication: 'Hubs', infrastructure: 'Hubs',
  appearance: 'Apparence',
}

export function UserDetailPage({ userId }: { userId: string }) {
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canManage = authenticated && permissions.includes('user.manage')

  const [detail, setDetail] = useState<Detail | null>(null)
  const [introuvable, setIntrouvable] = useState(false)
  const [tab, setTab] = useState('compte')
  const [audit, setAudit] = useState<{ total: number; items: AuditItem[] } | null>(null)
  const canWriteTemplates = authenticated && permissions.includes('role.write')
  const [rights, setRights] = useState<RightsOverview | null>(null)
  const [rightsError, setRightsError] = useState<string | null>(null)
  const [rightsTick, setRightsTick] = useState(0)
  const [applyOpen, setApplyOpen] = useState(false)
  const [saveAsOpen, setSaveAsOpen] = useState(false)
  const [confirm, setConfirm] = useState<{ title: string; body: string; danger: boolean; action: () => Promise<void> } | null>(null)

  const charger = useCallback(() => {
    apiGet<Detail>(`/api/v1/admin/users/${userId}`)
      .then((d) => { if ((d as unknown as { error?: string }).error) setIntrouvable(true); else setDetail(d) })
      .catch(() => setIntrouvable(true))
  }, [userId])
  useEffect(() => { charger() }, [charger])
  // La fiche « d'où viennent ses droits » : calculée par le moteur, rechargée
  // après chaque changement de modèles.
  useEffect(() => {
    if (tab !== 'roles') return
    let alive = true
    apiGet<unknown>(`/api/v1/admin/users/${userId}/rights`).then((r) => {
      if (!alive) return
      const o = shapeRights(r)
      if (o) { setRights(o); setRightsError(null) }
      else setRightsError(typeof r === 'object' && r !== null && typeof (r as { message?: unknown }).message === 'string' ? (r as { message: string }).message : 'Réponse inattendue du serveur.')
    }).catch(() => { if (alive) setRightsError('Le serveur n’a pas répondu.') })
    return () => { alive = false }
  }, [tab, userId, rightsTick])
  const rechargerDroits = () => { charger(); setRightsTick((n) => n + 1) }
  useEffect(() => {
    if (tab === 'audit' && !audit) {
      apiGet<{ total: number; items: AuditItem[] }>(`/api/v1/admin/users/${userId}/audit?limit=30`)
        .then(setAudit).catch(() => undefined)
    }
  }, [tab, audit, userId])

  const mutate = async (method: string, path: string, body?: unknown): Promise<boolean> => {
    const r = await apiMutate<{ error?: string; message?: string }>(method, path, body)
    if (r.status >= 200 && r.status < 300) return true
    if (r.data.error === 'STEPUP_REQUIRED') toast('error', 'Opération sensible : une ré-authentification forte récente est requise.')
    else toast('error', r.data.message ?? 'La modification n’a pas été enregistrée. Rien n’a changé.')
    return false
  }

  if (introuvable) {
    return (
      <main className="lc-page lc-stagger">
        <Link href="/settings/users" className="ud-back"><ArrowLeft size={15} /> Utilisateurs</Link>
        <h1 className="lc-title">Utilisateur introuvable</h1>
        <p className="lc-subtitle">Ce compte n’existe pas — il a peut-être été créé puis purgé en recette.</p>
      </main>
    )
  }
  if (!detail) return <main className="lc-page lc-stagger"><p className="lc-subtitle">Chargement…</p></main>

  const u = detail.user
  const grouper = (keys: string[]) => keys.reduce<Record<string, string[]>>((acc, p) => {
    const g = permMeta(p).group ?? PERM_GROUP_LABELS[p.split('.')[0] ?? ''] ?? 'Autres'
    ;(acc[g] ??= []).push(p)
    return acc
  }, {})
  // Affectation (user_role) d'un côté, moteur de l'autre — jamais mélangés.
  const assignedRoles = shapeAssignedRoles(detail.assigned_roles)
  const effective = shapeEffective(detail.effective)
  const scopedByKey = new Map((effective?.scoped ?? []).map((s) => [s.permission, s]))
  const groupedAllowed = grouper(effective?.allowed ?? [])

  return (
    <main className="lc-page lc-stagger">
      <Link href="/settings/users" className="ud-back"><ArrowLeft size={15} /> Utilisateurs</Link>

      {/* ═══ Header identitaire ═══ */}
      <header className="ud-head">
        <span className="lc-avatar ud-avatar">
          {u.display_name.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
        </span>
        <div className="ud-head__main">
          <h1 className="lc-title" style={{ marginBottom: 2 }}>{u.display_name}</h1>
          <p className="ud-head__mail">{u.email}</p>
          <div className="ud-head__pills">
            {statutPill(u.status)}
            {u.mfa_enabled
              ? <span className="lc-pill lc-pill--ok"><ShieldCheck size={13} />MFA actif</span>
              : <span className="lc-pill lc-pill--idle">{u.mfa_required ? 'MFA à activer' : 'MFA non requis'}</span>}
            {u.roles.map((r) => <span key={r.key} className="lc-pill lc-pill--idle">{r.name}</span>)}
          </div>
        </div>
        <div className="ud-head__aside">
          <span className="ad-fact__label">Dernière connexion</span>
          <span className="u-mono" style={{ fontSize: 'var(--fs-14)' }}>{fmt(u.last_login_at)}</span>
        </div>
      </header>

      <Tabs
        tabs={[
          { key: 'compte', label: 'Compte' },
          { key: 'securite', label: 'Sécurité' },
          { key: 'roles', label: 'Droits' },
          { key: 'notifications', label: 'Notifications' },
          { key: 'sessions', label: `Sessions (${detail.sessions.length})` },
          { key: 'acces', label: 'Accès futurs' },
          { key: 'audit', label: 'Audit' },
        ]}
        active={tab}
        onChange={setTab}
      />

      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }}>
        {tab === 'compte' ? (
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            <div className="ad-fact"><span className="ad-fact__label">Nom affiché</span><span className="ad-fact__value">{u.display_name}</span></div>
            <div className="ad-fact"><span className="ad-fact__label">E-mail</span><span className="ad-fact__value u-mono" style={{ fontWeight: 400 }}>{u.email}</span></div>
            <div className="ad-fact"><span className="ad-fact__label">État</span><span className="ad-fact__value">{statutPill(u.status)}</span></div>
            <div className="ad-fact"><span className="ad-fact__label">Créé le</span><span className="ad-fact__value" style={{ fontWeight: 400 }}>{fmt(u.created_at)}</span></div>
            <div className="ad-fact"><span className="ad-fact__label">Dernière connexion</span><span className="ad-fact__value" style={{ fontWeight: 400 }}>{fmt(u.last_login_at)}</span></div>
            <div className="ad-fact"><span className="ad-fact__label">Sessions actives</span><span className="ad-fact__value">{u.active_sessions}</span></div>
          </div>
        ) : null}

        {tab === 'securite' ? (
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            <div className="ad-fact">
              <span className="ad-fact__label">Authentification à deux facteurs</span>
              <span className="ad-fact__value">
                {u.mfa_enabled
                  ? <span className="lc-pill lc-pill--ok"><ShieldCheck size={13} />TOTP confirmé</span>
                  : <span className="lc-pill lc-pill--warning">{u.mfa_required ? 'Requise — pas encore activée' : 'Non requise'}</span>}
              </span>
            </div>
            <div className="ad-fact">
              <span className="ad-fact__label">Mot de passe</span>
              <span className="ad-fact__value">
                {detail.security.has_password
                  ? detail.security.must_change_password
                    ? <span className="lc-pill lc-pill--warning">Temporaire — changement forcé à la connexion</span>
                    : <span className="lc-pill lc-pill--ok">Défini{detail.security.password_updated_at ? ` · ${fmt(detail.security.password_updated_at)}` : ''}</span>
                  : <span className="lc-pill lc-pill--idle">Aucun identifiant — activation à finaliser</span>}
              </span>
            </div>
            <div className="ad-fact">
              <span className="ad-fact__label">Codes de secours restants</span>
              <span className="ad-fact__value">
                <span className={`lc-pill ${detail.security.recovery_codes_remaining <= 2 && u.mfa_enabled ? 'lc-pill--warning' : 'lc-pill--idle'}`}>
                  {detail.security.recovery_codes_remaining}
                </span>
              </span>
            </div>
            <div className="ad-fact">
              <span className="ad-fact__label">Réinitialisation MFA</span>
              <span className="ad-fact__value" style={{ fontWeight: 400, color: 'var(--text-2)', textAlign: 'right', maxWidth: 460 }}>
                Pas de bouton ici volontairement : la procédure opérateur
                (vérification d’identité comprise) est décrite dans le runbook
                <span className="u-mono"> auth-lockout-recovery</span>.
              </span>
            </div>
          </div>
        ) : null}

        {tab === 'roles' ? (
          <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
            {/* ═══ BLOC 1 — MODÈLES APPLIQUÉS (donnée d'affectation, §28) ═══ */}
            <div className="ad-perm-group__title">Modèles appliqués</div>
            <p className="dr-block__intro">
              Les modèles de droits donnés à ce compte : il reçoit tout ce qu’ils accordent.
              C’est une donnée d’affectation : elle ne dit pas, seule, ce que le compte peut
              réellement faire — voir « Modules » et « Droits effectifs ».
            </p>
            {assignedRoles !== null ? (
              assignedRoles.length === 0
                ? <p className="dr-hint">Aucun modèle — le compte n’a que ses exceptions éventuelles.</p>
                : (
                  <div className="dr-applied">
                    {assignedRoles.map((r) => {
                      const t = rights?.templates.find((x) => x.key === r.key)
                      return (
                        <div key={r.key} className="dr-applied__item">
                          <span className="dr-applied__name">
                            {r.name}{' '}
                            {r.key === 'super_admin' ? <Lock size={12} aria-label="protégé" /> : t?.suggested ? <Sparkles size={12} aria-label="proposé" /> : null}
                          </span>
                          {t?.description ? <span className="dr-applied__desc">{t.description}</span> : null}
                          <span className="dr-applied__meta">{r.key === 'super_admin' ? 'Tout le catalogue' : <>{r.permissions} permission{r.permissions > 1 ? 's' : ''}</>}</span>
                        </div>
                      )
                    })}
                  </div>
                )
            ) : (
              <p style={{ fontSize: 'var(--fs-14)' }}>{u.roles.map((r) => r.name).join(', ') || 'Aucun modèle.'}</p>
            )}
            <div className="dr-block__actions">
              {canManage ? (
                <button type="button" className="lc-btn" onClick={() => setApplyOpen(true)}>
                  <KeyRound size={14} /> Changer les modèles
                </button>
              ) : null}
              {canWriteTemplates ? (
                <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setSaveAsOpen(true)}>
                  <BookmarkPlus size={14} /> Enregistrer comme modèle
                </button>
              ) : null}
              <Link href="/settings/roles" className="lc-btn lc-btn--ghost">Voir tous les modèles</Link>
            </div>

            {/* ═══ BLOC 2 — MODULES (déduits des droits, jamais stockés) ═══ */}
            <div className="ad-perm-group__title" style={{ marginTop: 'var(--sp-6)' }}>Modules</div>
            <p className="dr-block__intro">
              Ce que ses droits ouvrent, famille par famille : un module est accessible
              quand au moins un droit y est accordé et qu’aucun refus ne le ferme.
            </p>
            <UserModulesBlock overview={rights} error={rightsError} />

            {/* ═══ BLOC 3 — EXCEPTIONS (cases par permission, qui l'emportent) ═══ */}
            <div className="ad-perm-group__title" style={{ marginTop: 'var(--sp-6)' }}>Exceptions</div>
            <p className="dr-block__intro">
              Des cases posées sur cette personne seule, droit par droit : un « Autorisé »
              ajoute un droit, un « Refusé » en retire un — elles l’emportent sur les modèles.
            </p>
            <UserExceptionsBlock overview={rights} userId={u.id} />

            {/* ═══ BLOC 4 — DROITS EFFECTIFS (moteur, §29-31) ═══ */}
            <div className="ad-perm-group__title" style={{ marginTop: 'var(--sp-6)' }}>
              Droits effectifs
              {effective ? ` (${effective.allowed.length} autorisé${effective.allowed.length > 1 ? 's' : ''} · ${effective.denied_by_override.length + effective.blocked_by_gate.length} refusé${effective.denied_by_override.length + effective.blocked_by_gate.length > 1 ? 's' : ''})` : ''}
            </div>
            <p style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)', margin: '0 0 var(--sp-3)' }}>
              Ce que le moteur d’autorisation accorde réellement : gates de famille et de
              module, refus directs et périmètres compris. Un rôle peut accorder un droit
              qu’un refus direct retire — c’est ici que ça se voit.
            </p>

            {effective === null ? (
              <>
                <p className="adm-note" style={{ marginTop: 0 }}>
                  <strong>Droits effectifs indisponibles.</strong> Le serveur n’a pas renvoyé le
                  résultat du moteur pour ce compte. Les rôles ci-dessus restent une donnée
                  d’affectation : ils ne disent pas ce que ce compte peut faire, et cet écran
                  ne l’invente pas.
                </p>
                {detail.permissions.length > 0 ? (
                  <p style={{ fontSize: 'var(--fs-13)', color: 'var(--text-3)', marginTop: 'var(--sp-2)' }}>
                    {detail.permissions.length} permission{detail.permissions.length > 1 ? 's' : ''} portée
                    {detail.permissions.length > 1 ? 's' : ''} par ces rôles (affectation) — statut effectif inconnu.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {effective.allowed.length === 0 && effective.denied_by_override.length === 0 && effective.blocked_by_gate.length === 0 ? (
                  <p className="lc-card__desc">Aucun droit effectif — le compte ne peut que consulter ce que l’enforcement laissera ouvert.</p>
                ) : null}

                {Object.entries(groupedAllowed).map(([g, list]) => (
                  <div key={g} style={{ marginBottom: 'var(--sp-3)' }}>
                    <div style={{ fontSize: 'var(--fs-13)', color: 'var(--text-3)', marginBottom: 4 }}>{g}</div>
                    <div className="lc-stack" style={{ gap: 5 }}>
                      {list.map((p) => {
                        const s = scopedByKey.get(p)
                        return (
                          <div key={p} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
                            <span style={{ fontSize: 'var(--fs-14)' }} title={permMeta(p).description}>{s?.label ?? permMeta(p).label}</span>
                            <span style={{ fontSize: 'var(--fs-13)', color: 'var(--emerald)' }}>{effectiveSentence(s)}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}

                {effective.denied_by_override.length > 0 ? (
                  <div style={{ marginBottom: 'var(--sp-3)' }}>
                    <div style={{ fontSize: 'var(--fs-13)', color: 'var(--text-3)', marginBottom: 4 }}>
                      Refusés directement — un rôle les accorde, un refus individuel les retire
                    </div>
                    <div className="lc-stack" style={{ gap: 5 }}>
                      {effective.denied_by_override.map((p) => (
                        <div key={p} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ fontSize: 'var(--fs-14)' }} title={permMeta(p).description}>{permMeta(p).label}</span>
                          <span style={{ fontSize: 'var(--fs-13)', color: 'var(--rose)' }}>{DENIED_SENTENCE}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {effective.blocked_by_gate.length > 0 ? (
                  <div style={{ marginBottom: 'var(--sp-3)' }}>
                    <div style={{ fontSize: 'var(--fs-13)', color: 'var(--text-3)', marginBottom: 4 }}>
                      Bloqués par un accès de famille ou de module
                    </div>
                    <div className="lc-stack" style={{ gap: 5 }}>
                      {effective.blocked_by_gate.map((p) => (
                        <div key={p} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ fontSize: 'var(--fs-14)' }} title={permMeta(p).description}>{permMeta(p).label}</span>
                          <span style={{ fontSize: 'var(--fs-13)', color: 'var(--rose)' }}>{GATED_SENTENCE}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {/* ═══ BLOC 5 — HISTORIQUE (modèles et exceptions, avec leur motif) ═══ */}
            <div className="ad-perm-group__title" style={{ marginTop: 'var(--sp-6)' }}>Historique des droits</div>
            <UserRightsHistory userId={u.id} overview={rights} reloadKey={rightsTick} />
          </div>
        ) : null}

        {tab === 'notifications' ? (
          // Le panneau porte son titre et son introduction : rien à redire autour.
          <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
            <UserNotificationPrefsPanel userId={u.id} />
          </div>
        ) : null}

        {tab === 'sessions' ? (
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            {detail.sessions.length === 0 ? (
              <div className="ad-fact"><span className="ad-fact__label">Aucune session active.</span></div>
            ) : detail.sessions.map((s) => (
              <div key={s.id} className="ad-fact">
                <span>
                  <div style={{ fontSize: 'var(--fs-14)', fontWeight: s.current ? 600 : 400 }}>
                    {deviceSummary(s.user_agent)}{s.current ? ' — votre session' : ''}
                  </div>
                  <div className="ad-mono" style={{ color: 'var(--text-3)' }}>
                    {s.ip ?? 'IP inconnue'} · active {fmt(s.last_seen_at)} · expire {fmt(s.expires_at)}
                  </div>
                </span>
                {canManage && !s.current ? (
                  <button className="lc-btn lc-btn--ghost" onClick={async () => {
                    if (await mutate('POST', `/api/v1/admin/users/${u.id}/sessions/${s.id}/revoke`)) {
                      toast('ok', 'Session révoquée'); charger()
                    }
                  }}>Révoquer</button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {tab === 'acces' ? (
          <div className="lc-panel__body" style={{ padding: 'var(--sp-5) var(--sp-6)' }}>
            <div className="adm-note" style={{ marginTop: 0 }}>
              <strong>Préparation future.</strong> Gestion connaît les catalogues de
              permissions des applications, mais n’y applique encore aucun droit —
              l’administration centralisée arrivera avec le chantier SSO. Rien
              n’est cochable ici tant que ce n’est pas réel.
            </div>
            {detail.service_catalogs.map((c) => (
              <div key={c.service_key} className="ad-fact">
                <span className="ad-fact__label">{c.service_name}</span>
                <span className="ad-fact__value" style={{ color: 'var(--text-2)', fontWeight: 400 }}>
                  Catalogue disponible · {c.permissions} permissions · Gestion non autoritaire
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {tab === 'audit' ? (
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            {!audit ? (
              <div className="ad-fact"><span className="ad-fact__label">Chargement…</span></div>
            ) : audit.items.length === 0 ? (
              <div className="ad-fact"><span className="ad-fact__label">Aucun événement pour ce compte.</span></div>
            ) : (
              <>
                {audit.items.map((e) => (
                  <div key={e.id} className="ad-fact">
                    <span>
                      <div style={{ fontSize: 'var(--fs-14)' }}>
                        <strong>{auditMeta(e.event_type).label}</strong>
                        {e.human_message ? <span style={{ color: 'var(--text-2)' }}> — {e.human_message}</span> : null}
                      </div>
                      {/* Plancher 13 px (DS §4) : cette ligne était à 12 px. */}
                      <div style={{ color: 'var(--text-3)', fontSize: 'var(--fs-13)' }}>
                        {fmt(e.occurred_at)} · {e.is_actor ? 'à l’initiative du compte' : 'le compte est concerné'}
                      </div>
                    </span>
                    {e.severity !== 'INFO' ? <span className="lc-pill lc-pill--warning">{e.severity}</span> : null}
                  </div>
                ))}
                <div className="ad-fact">
                  <span className="ad-fact__label">{audit.total} événement{audit.total > 1 ? 's' : ''} au total</span>
                  <Link href="/audit" className="lc-btn lc-btn--ghost"><ScrollText size={14} /> Voir tout dans Audit</Link>
                </div>
              </>
            )}
          </div>
        ) : null}
      </section>

      {/* ═══ Actions sensibles — en BAS, chacune explique sa conséquence ═══ */}
      {canManage ? (
        <section className="lc-panel gd-panel ud-danger" aria-labelledby="ud-danger-title">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="ud-danger-title">Actions sensibles</h2>
          </header>
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            {detail.sessions.length > 0 ? (
              <div className="ad-fact">
                <span>
                  <div style={{ fontSize: 'var(--fs-14)', fontWeight: 600 }}>Révoquer toutes les sessions</div>
                  <div style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)' }}>
                    Déconnecte {u.display_name} de tous ses appareils immédiatement. Le compte reste actif.
                  </div>
                </span>
                <button className="lc-btn" onClick={() => setConfirm({
                  title: 'Révoquer toutes les sessions ?',
                  body: `${u.display_name} sera déconnecté de tous ses appareils immédiatement. Le compte reste actif — il pourra se reconnecter.`,
                  danger: false,
                  action: async () => {
                    if (await mutate('POST', `/api/v1/admin/users/${u.id}/sessions/revoke-all`)) {
                      toast('ok', 'Toutes les sessions révoquées'); charger()
                    }
                  },
                })}>
                  <LogOut size={14} /> Révoquer
                </button>
              </div>
            ) : null}
            <div className="ad-fact">
              <span>
                <div style={{ fontSize: 'var(--fs-14)', fontWeight: 600 }}>
                  {u.status !== 'DISABLED' ? 'Désactiver le compte' : 'Réactiver le compte'}
                </div>
                <div style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)' }}>
                  {u.status !== 'DISABLED'
                    ? 'Connexion impossible et sessions révoquées. Le compte et son historique sont conservés — réversible.'
                    : 'Le compte pourra de nouveau se connecter, avec ses rôles inchangés.'}
                </div>
              </span>
              {u.status !== 'DISABLED' ? (
                <button className="lc-btn lc-btn--danger" onClick={() => setConfirm({
                  title: `Désactiver ${u.display_name} ?`,
                  body: 'Le compte ne pourra plus se connecter et TOUTES ses sessions actives seront révoquées immédiatement. Le compte et son historique sont conservés — cette action est réversible.',
                  danger: true,
                  action: async () => {
                    if (await mutate('POST', `/api/v1/admin/users/${u.id}/disable`)) {
                      toast('ok', 'Compte désactivé — sessions révoquées'); charger()
                    }
                  },
                })}>
                  <Power size={14} /> Désactiver
                </button>
              ) : (
                <button className="lc-btn lc-btn--primary" onClick={async () => {
                  if (await mutate('POST', `/api/v1/admin/users/${u.id}/enable`)) {
                    toast('ok', 'Compte réactivé'); charger()
                  }
                }}>
                  <Power size={14} /> Réactiver
                </button>
              )}
            </div>
          </div>
        </section>
      ) : null}

      <ApplyTemplatesModal
        open={applyOpen}
        userId={u.id}
        userName={u.display_name}
        current={u.roles.map((r) => r.key)}
        onClose={() => setApplyOpen(false)}
        onApplied={(message) => { setApplyOpen(false); toast('ok', message); rechargerDroits() }}
      />
      <SaveAsTemplateModal
        open={saveAsOpen}
        userId={u.id}
        userName={u.display_name}
        rightsCount={effective ? effective.allowed.length : null}
        onClose={() => setSaveAsOpen(false)}
        onSaved={(key, name) => {
          setSaveAsOpen(false)
          toast('ok', `Modèle « ${name} » créé à partir des droits de ${u.display_name}.`, { action: { label: 'Ouvrir le modèle', onAction: () => navigate(`/settings/roles/${key}/permissions`) } })
        }}
      />

      <ConfirmDialog
        open={confirm !== null}
        danger={confirm?.danger ?? false}
        title={confirm?.title ?? ''}
        confirmLabel="Confirmer"
        onConfirm={() => { const c = confirm; setConfirm(null); void c?.action() }}
        onClose={() => setConfirm(null)}
      >
        {confirm?.body}
      </ConfirmDialog>
    </main>
  )
}
